/**
 * AuthCredentialService
 *
 * Manages cached authentication credentials for UIA (User Interactive Auth).
 * Supports both password-based and SSO authentication methods.
 *
 * Security: Credentials are held in memory only, cleared after 10 minutes,
 * and explicitly cleared on logout.
 *
 * This pattern matches the reference Dart client (FluffyChat) which caches
 * the password for 10 minutes after login to enable initial bootstrap
 * without re-prompting the user.
 */

import logger from '../utils/logger';

const CREDENTIAL_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// Authentication method types
export const AuthMethod = {
  PASSWORD: 'password',
  SSO: 'sso',
};

// Private state (module-scoped, not exported)
let _authMethod = null;           // 'password' | 'sso'
let _cachedPassword = null;       // For password auth
let _cacheTimer = null;
let _userId = null;               // Cached user ID for UIA

/**
 * Cache password credential after successful password login
 * @param {string} password - The user's password
 * @param {string} userId - The Matrix user ID (e.g., @user:server.com)
 */
export function cachePasswordCredential(password, userId) {
  clearCredentials();

  _authMethod = AuthMethod.PASSWORD;
  _cachedPassword = password;
  _userId = userId;

  _startCacheTimer();

  logger.debug('authCredentialService: Password credential cached for UIA');
}

/**
 * Cache SSO session info after successful SSO login
 * Note: SSO doesn't cache a reusable credential - it stores metadata
 * to know we need to trigger SSO flow for UIA
 * @param {string} userId - The Matrix user ID
 */
export function cacheSsoSession(userId) {
  clearCredentials();

  _authMethod = AuthMethod.SSO;
  _userId = userId;

  _startCacheTimer();

  logger.debug('authCredentialService: SSO session info cached for UIA');
}

/**
 * Get the cached authentication method
 * @returns {'password' | 'sso' | null}
 */
export function getAuthMethod() {
  return _authMethod;
}

/**
 * Get cached password (only valid for password auth)
 * @returns {string | null} The cached password or null
 */
export function getCachedPassword() {
  if (_authMethod !== AuthMethod.PASSWORD) {
    return null;
  }
  return _cachedPassword;
}

/**
 * Get cached user ID
 * @returns {string | null}
 */
export function getCachedUserId() {
  return _userId;
}

/**
 * Check if we have valid cached credentials
 * @returns {boolean}
 */
export function hasValidCredentials() {
  return _authMethod !== null;
}

/**
 * Check if SSO reauthentication is needed
 * @returns {boolean}
 */
export function needsSsoReauth() {
  return _authMethod === AuthMethod.SSO;
}

/**
 * Check whether the current session was established via SSO.
 *
 * Falls back to the persistent `ssoLoginEmail` marker written by the SSO login
 * flow when the in-memory cache has already expired (TTL = 10 minutes).
 * The marker is cleared on logout, so it reliably indicates SSO for the lifetime
 * of the logged-in session.
 *
 * @returns {boolean}
 */
export function isSsoLoggedInSession() {
  if (_authMethod === AuthMethod.SSO) return true;
  try {
    return typeof localStorage !== 'undefined' && !!localStorage.getItem('ssoLoginEmail');
  } catch {
    return false;
  }
}

/**
 * Clear all cached credentials
 */
export function clearCredentials() {
  _authMethod = null;
  _cachedPassword = null;
  _userId = null;

  if (_cacheTimer) {
    clearTimeout(_cacheTimer);
    _cacheTimer = null;
  }

  logger.debug('authCredentialService: Credentials cleared');
}

/**
 * Start the cache expiration timer (private)
 */
function _startCacheTimer() {
  if (_cacheTimer) {
    clearTimeout(_cacheTimer);
  }

  _cacheTimer = setTimeout(() => {
    logger.debug('authCredentialService: Credential cache expired after 10 minutes');
    clearCredentials();
  }, CREDENTIAL_CACHE_TTL);
}

/**
 * Extend the cache timer (call after successful UIA)
 */
export function extendCacheTimer() {
  if (_authMethod) {
    _startCacheTimer();
    logger.debug('authCredentialService: Credential cache timer extended');
  }
}
